import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, IconButton, Tooltip, TextField, InputAdornment,
    Select, MenuItem, FormControl, InputLabel, CircularProgress, useTheme,
    useMediaQuery, Button, Drawer, Divider, Snackbar, Alert, TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';

const FRONTEND_URL = window.location.origin;

function StatusChip({ assinado }) {
    return assinado
        ? <Chip label="Assinado" color="success" size="small" />
        : <Chip label="Pendente" color="warning" size="small" />;
}

export default function HistoricoDocumentos() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [documentos, setDocumentos] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [filtroPaciente, setFiltroPaciente] = useState('');
    const [buscaPaciente, setBuscaPaciente] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('Todos');
    const [filtroTipo, setFiltroTipo] = useState('Todos');

    /** Rótulo por tipo, servido pela API — o front não mantém mais essa lista. */
    const [rotulosTipo, setRotulosTipo] = useState({});

    // Gaveta de detalhe. A listagem não traz o HTML do documento (seria o arquivo
    // de cada linha, com o logo em base64); o conteúdo é buscado ao abrir.
    const [detalhe, setDetalhe] = useState(null);
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

    const [copiado, setCopiado] = useState(false);
    const [enviando, setEnviando] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        api.get('/documentos/metadados')
            .then(res => {
                const mapa = {};
                (res.data?.tipos || []).forEach(t => { mapa[t.valor] = t.rotulo; });
                setRotulosTipo(mapa);
            })
            .catch(() => { /* a tabela cai no nome do enum */ });
    }, []);

    // Debounce da busca por paciente: agora cada tecla seria uma consulta.
    useEffect(() => {
        const t = setTimeout(() => { setBuscaPaciente(filtroPaciente); setPage(0); }, 350);
        return () => clearTimeout(t);
    }, [filtroPaciente]);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, size: rowsPerPage };
            if (buscaPaciente) params.paciente = buscaPaciente;
            if (filtroTipo !== 'Todos') params.tipo = filtroTipo;
            if (filtroStatus === 'Assinado') params.assinado = true;
            if (filtroStatus === 'Pendente') params.assinado = false;

            const res = await api.get('/documentos', { params });
            setDocumentos(res.data?.content ?? []);
            setTotalElements(res.data?.totalElements ?? 0);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, buscaPaciente, filtroTipo, filtroStatus]);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => { setPage(0); }, [filtroTipo, filtroStatus]);

    const rotuloTipo = (tipo) => rotulosTipo[tipo] || tipo || '—';

    const abrirDetalhe = async (doc) => {
        setDetalhe({ ...doc, conteudoFinal: null });
        setCarregandoDetalhe(true);
        try {
            const res = await api.get(`/documentos/${doc.id}`);
            setDetalhe(res.data);
        } catch {
            setSnackbar({ open: true, severity: 'error', message: 'Não foi possível abrir o documento.' });
            setDetalhe(null);
        } finally {
            setCarregandoDetalhe(false);
        }
    };

    const linkAceite = (doc) => `${FRONTEND_URL}/aceite/${doc.tokenAceite}`;

    const copiarLink = (doc) => {
        navigator.clipboard.writeText(linkAceite(doc));
        setCopiado(doc.id);
        setTimeout(() => setCopiado(false), 2000);
    };

    const notificar = async (doc, canal) => {
        const key = `${doc.id}-${canal}`;
        setEnviando(key);
        try {
            const res = await api.post(`/documentos/${doc.id}/notificar/${canal}`);
            const ok = res.data?.status === 'ENVIADA';
            setSnackbar({
                open: true,
                severity: ok ? 'success' : 'error',
                message: ok
                    ? `Notificação enviada via ${canal === 'WHATSAPP' ? 'WhatsApp' : 'e-mail'}.`
                    : `Falha: ${res.data?.mensagemErro || 'erro desconhecido'}`,
            });
        } catch (err) {
            setSnackbar({
                open: true,
                severity: 'error',
                message: err.response?.data?.message || `Erro ao notificar via ${canal}.`,
            });
        } finally {
            setEnviando(null);
        }
    };

    const handleDownload = async (doc) => {
        try {
            const res = await api.get(`/documentos/${doc.id}/download`, { responseType: 'blob' });
            const contentType = res.headers['content-type'] || 'application/pdf';
            const blob = new Blob([res.data], { type: contentType });
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) {
                const a = document.createElement('a');
                a.href = url;
                a.download = doc.numeroDocumento ? `${doc.numeroDocumento}.pdf` : 'documento.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            setSnackbar({ open: true, severity: 'error', message: 'Não foi possível baixar o PDF.' });
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333', mb: 3 }}>
                Histórico de Documentos
            </Typography>

            <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                {/* Filtros — resolvidos no servidor, junto com a paginação. */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Buscar por paciente..."
                        size="small"
                        value={filtroPaciente}
                        onChange={e => setFiltroPaciente(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
                        sx={{ flex: '1 1 200px' }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Tipo</InputLabel>
                        <Select value={filtroTipo} label="Tipo" onChange={e => setFiltroTipo(e.target.value)}>
                            <MenuItem value="Todos">Todos</MenuItem>
                            {Object.entries(rotulosTipo).map(([valor, rotulo]) => (
                                <MenuItem key={valor} value={valor}>{rotulo}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={filtroStatus} label="Status" onChange={e => setFiltroStatus(e.target.value)}>
                            <MenuItem value="Todos">Todos</MenuItem>
                            <MenuItem value="Assinado">Assinado</MenuItem>
                            <MenuItem value="Pendente">Pendente</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <TableContainer>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell><strong>Nº</strong></TableCell>
                                <TableCell><strong>Tipo</strong></TableCell>
                                <TableCell><strong>Paciente</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Emissão</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Aceite</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <CircularProgress sx={{ color: '#1ABB9C' }} />
                                    </TableCell>
                                </TableRow>
                            ) : documentos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">Nenhum documento encontrado.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : documentos.map(doc => (
                                <TableRow
                                    key={doc.id}
                                    hover
                                    onClick={() => abrirDetalhe(doc)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{doc.numeroDocumento}</TableCell>
                                    <TableCell>
                                        <Chip label={rotuloTipo(doc.tipoDocumento)} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{doc.nomePaciente}</TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        {doc.dataEmissao ? new Date(doc.dataEmissao).toLocaleDateString('pt-BR') : '-'}
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        {doc.dataAssinatura ? new Date(doc.dataAssinatura).toLocaleString('pt-BR') : '-'}
                                    </TableCell>
                                    <TableCell><StatusChip assinado={doc.assinado} /></TableCell>
                                    <TableCell align="right">
                                        {/* O clique na linha abre o detalhe; os botões não devem propagar. */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }} onClick={e => e.stopPropagation()}>
                                            <Tooltip title="Ver documento">
                                                <IconButton size="small" onClick={() => abrirDetalhe(doc)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Baixar PDF">
                                                <IconButton size="small" onClick={() => handleDownload(doc)} color="primary">
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            {!doc.assinado && doc.tokenAceite && (
                                                <>
                                                    <Tooltip title={copiado === doc.id ? 'Copiado!' : 'Copiar link de aceite'}>
                                                        <IconButton size="small" onClick={() => copiarLink(doc)} color={copiado === doc.id ? 'success' : 'default'}>
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Enviar via WhatsApp">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => notificar(doc, 'WHATSAPP')}
                                                                disabled={enviando === `${doc.id}-WHATSAPP`}
                                                                sx={{ color: '#25D366' }}
                                                            >
                                                                {enviando === `${doc.id}-WHATSAPP`
                                                                    ? <CircularProgress size={16} color="inherit" />
                                                                    : <WhatsAppIcon fontSize="small" />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Enviar via E-mail">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => notificar(doc, 'EMAIL')}
                                                                disabled={enviando === `${doc.id}-EMAIL`}
                                                                color="primary"
                                                            >
                                                                {enviando === `${doc.id}-EMAIL`
                                                                    ? <CircularProgress size={16} color="inherit" />
                                                                    : <EmailIcon fontSize="small" />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, nova) => setPage(nova)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Gaveta de detalhe */}
            <Drawer
                anchor="right"
                open={!!detalhe}
                onClose={() => setDetalhe(null)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 720 } } }}
            >
                {detalhe && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography fontWeight="bold">{detalhe.finalidade}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Nº {detalhe.numeroDocumento} · {rotuloTipo(detalhe.tipoDocumento)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {detalhe.nomePaciente}
                                    {detalhe.destinatario ? ` · para ${detalhe.destinatario}` : ''}
                                </Typography>
                            </Box>
                            <StatusChip assinado={detalhe.assinado} />
                            <IconButton size="small" onClick={() => setDetalhe(null)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Divider />

                        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#ECEFF1', p: 2 }}>
                            {carregandoDetalhe ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                    <CircularProgress sx={{ color: '#1ABB9C' }} />
                                </Box>
                            ) : (
                                /* O documento emitido é HTML já com as variáveis resolvidas.
                                   Vai num iframe com sandbox vazio: fiel ao que foi emitido
                                   (sem reaplicar o cabeçalho atual, que pode ter mudado
                                   desde então) e sem executar nada do conteúdo. */
                                <Box
                                    component="iframe"
                                    title="Documento emitido"
                                    sandbox=""
                                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"/>`
                                        + `<style>body{font-family:'Times New Roman',serif;font-size:11pt;`
                                        + `line-height:1.6;margin:0;padding:32px 40px;background:#fff;color:#222}`
                                        + `img{max-width:100%;height:auto}`
                                        + `table{border-collapse:collapse;width:100%!important}`
                                        + `td,th{border:1px solid #999;padding:5px 8px}</style></head><body>`
                                        + `${detalhe.cabecalhoFinal || ''}${detalhe.conteudoFinal || ''}`
                                        + `</body></html>`}
                                    sx={{
                                        width: '100%', minHeight: 640, border: 'none',
                                        bgcolor: '#fff', borderRadius: 1,
                                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                                    }}
                                />
                            )}
                        </Box>

                        <Divider />

                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setDetalhe(null)} color="inherit">Fechar</Button>
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleDownload(detalhe)}>
                                Baixar PDF
                            </Button>
                        </Box>
                    </Box>
                )}
            </Drawer>
        </Box>
    );
}
